import { WeatherData, WeatherCondition } from '../types/weather.types';

export const getWeatherCondition = (weatherCode: number, isDay: boolean): WeatherCondition => {
  // WMO Weather interpretation codes
  if (weatherCode === 0) return WeatherCondition.CLEAR;
  if (weatherCode === 1) return WeatherCondition.PARTLY_CLOUDY;
  if (weatherCode === 2) return WeatherCondition.PARTLY_CLOUDY;
  if (weatherCode === 3) return WeatherCondition.OVERCAST;
  if (weatherCode >= 45 && weatherCode <= 48) return WeatherCondition.FOG;
  if (weatherCode >= 51 && weatherCode <= 55) return WeatherCondition.DRIZZLE;
  if (weatherCode >= 61 && weatherCode <= 65) return WeatherCondition.RAIN;
  if (weatherCode >= 66 && weatherCode <= 67) return WeatherCondition.SLEET;
  if (weatherCode >= 71 && weatherCode <= 75) return WeatherCondition.SNOW;
  if (weatherCode >= 77) return WeatherCondition.SNOW;
  if (weatherCode >= 80 && weatherCode <= 82) return WeatherCondition.RAIN;
  if (weatherCode >= 85 && weatherCode <= 86) return WeatherCondition.SNOW;
  if (weatherCode >= 95 && weatherCode <= 99) return WeatherCondition.THUNDERSTORM;

  return WeatherCondition.CLOUDY;
};

export const getWeatherIconName = (weather: WeatherData): string => {
  if (weather.isSnowing) {
    if (weather.snowfall > 5) return 'cloud-snow';
    return 'cloud-snow';
  }

  if (weather.isRaining) {
    if (weather.precipitation > 5) return 'cloud-rain';
    if (weather.precipitation > 0.5) return 'cloud-rain';
    return 'cloud-drizzle';
  }

  const condition = getWeatherCondition(weather.weatherCode, weather.isDay);

  switch (condition) {
    case WeatherCondition.CLEAR:
      return weather.isDay ? 'sun' : 'moon';
    case WeatherCondition.PARTLY_CLOUDY:
      return weather.isDay ? 'cloud-sun' : 'moon';
    case WeatherCondition.CLOUDY:
      return 'cloud';
    case WeatherCondition.OVERCAST:
      return 'cloud';
    case WeatherCondition.FOG:
      return 'cloud-fog';
    case WeatherCondition.DRIZZLE:
      return 'cloud-drizzle';
    case WeatherCondition.THUNDERSTORM:
      return 'cloud-lightning';
    default:
      if (weather.temperature < 0) return 'thermometer';
      if (weather.temperature < 10) return 'cloud';
      if (weather.temperature < 20) return 'cloud-sun';
      if (weather.temperature < 30) return 'sun';
      return 'sun';
  }
};

export const getWeatherDescription = (weather: WeatherData): string => {
  if (weather.isSnowing) {
    if (weather.snowfall > 5) return 'Heavy Snow';
    return 'Snowing';
  }

  if (weather.isRaining) {
    if (weather.precipitation > 5) return 'Heavy Rain';
    if (weather.precipitation > 0.5) return 'Rainy';
    return 'Light Rain';
  }

  const condition = getWeatherCondition(weather.weatherCode, weather.isDay);

  switch (condition) {
    case WeatherCondition.CLEAR:
      return weather.isDay ? 'Clear Sky' : 'Clear Night';
    case WeatherCondition.PARTLY_CLOUDY:
      return 'Partly Cloudy';
    case WeatherCondition.CLOUDY:
      return 'Cloudy';
    case WeatherCondition.OVERCAST:
      return 'Overcast';
    case WeatherCondition.FOG:
      return 'Foggy';
    case WeatherCondition.DRIZZLE:
      return 'Drizzle';
    case WeatherCondition.THUNDERSTORM:
      return 'Thunderstorm';
    default:
      if (weather.temperature < 0) return 'Freezing';
      if (weather.temperature < 10) return 'Cold';
      if (weather.temperature < 20) return 'Cool';
      if (weather.temperature < 30) return 'Warm';
      return 'Hot';
  }
};

export const calculateCloudiness = (weather: WeatherData): number => {
  // Use weather code and cloud coverage
  const { weatherCode, cloudCoverage } = weather;

  if (cloudCoverage > 0) return cloudCoverage;

  // Estimate from weather code
  if (weatherCode === 0) return 0; // Clear
  if (weatherCode === 1) return 0.25; // Mainly clear
  if (weatherCode === 2) return 0.5; // Partly cloudy
  if (weatherCode === 3) return 0.85; // Overcast
  if (weatherCode >= 45) return 0.9; // Fog/precipitation

  return 0.3; // Default
};

export const formatTemperature = (temp: number, unit: 'C' | 'F' = 'C'): string => {
  if (unit === 'F') {
    const fahrenheit = (temp * 9/5) + 32;
    return `${fahrenheit.toFixed(1)}°F`;
  }
  return `${temp.toFixed(1)}°C`;
};

export const formatWindSpeed = (speed: number, unit: 'kmh' | 'mph' | 'ms' = 'kmh'): string => {
  if (unit === 'mph') {
    const mph = speed * 0.621371;
    return `${mph.toFixed(1)} mph`;
  }
  if (unit === 'ms') {
    const ms = speed / 3.6;
    return `${ms.toFixed(1)} m/s`;
  }
  return `${speed.toFixed(1)} km/h`;
};

export const getUVIndexLevel = (uvIndex: number): { level: string; color: string } => {
  if (uvIndex <= 2) return { level: 'Low', color: '#4CAF50' };
  if (uvIndex <= 5) return { level: 'Moderate', color: '#FFEB3B' };
  if (uvIndex <= 7) return { level: 'High', color: '#FF9800' };
  if (uvIndex <= 10) return { level: 'Very High', color: '#F44336' };
  return { level: 'Extreme', color: '#9C27B0' };
};

export const retry = async <T>(
  fn: () => Promise<T>,
  options: {
    maxAttempts?: number;
    delay?: number;
    backoff?: boolean;
  } = {}
): Promise<T> => {
  const {
    maxAttempts = 3,
    delay = 1000,
    backoff = true,
  } = options;

  let lastError: Error;

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error as Error;

      if (attempt < maxAttempts) {
        const waitTime = backoff ? delay * Math.pow(2, attempt - 1) : delay;
        await new Promise(resolve => setTimeout(resolve, waitTime));
      }
    }
  }

  throw lastError!;
};
